import * as React from 'react';
import styled from 'styled-components';

import { Menu, MenuSection, MenuSectionList } from './ExtendedMainMenu';
import { MenuNavigationItemTypeDesktopGroup } from '../types';
import { Size } from '../../types';
import { SystemIcons } from '../../icons';
import { checkIfContainsItem } from '../utils';
import Tabs from '../../Tabs/Tabs';
import TabLink from '../../Tabs/TabLink';
import { MenuItem } from '../../MenuItem';

export type SubMenuProps = {
  item: MenuNavigationItemTypeDesktopGroup;
  index: number;
  topLevel: boolean;
  dropdownMenusOpenedArray: React.MutableRefObject<string[]>;
  onSubMenuOpened?: (item: string) => void;
  onSubMenuHidden?: (item: string) => void;
  onItemStateChanged?: (item: string, state: boolean) => void;
  onItemFocused?: (item: string) => void;
  focusedItemId?: string;
  activeItemId?: string;
  nestPath?: string[];
  ariaLabelledBy?: string;
};

export const SubMenuWrapper = styled.div<{ $topLevel: boolean; $visible: boolean }>`
  ${Menu} {
    top: ${(props) => (props.$topLevel ? '100%' : '0px')};
    left: ${(props) => (props.$topLevel ? '0px' : 'calc(100% - 12px)')};
  }

  ${(props) => (!props.$visible ? 'display: none;' : '')}

  button {
    padding-left: 0px;
  }
`;

export const SubMenu = React.forwardRef<HTMLUListElement, SubMenuProps>(({
  item,
  index,
  topLevel,
  dropdownMenusOpenedArray,
  onSubMenuHidden,
  onSubMenuOpened,
  onItemStateChanged,
  focusedItemId,
  activeItemId,
  nestPath,
  ariaLabelledBy,
  onItemFocused
}: SubMenuProps, ref): React.ReactElement<SubMenuProps> => {
  const [, forceUpdate] = React.useState({});
  const focusedRef = React.useRef<{ index: number }>({ index: -1 });
  const [allRefs, setAllRefs] = React.useState<React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>[]>([]);
  const [subMenuRefs, setAllSubMenuRefs] = React.useState<(React.RefObject<HTMLUListElement | null> | undefined)[]>([]);

  React.useEffect(() => {
    setAllRefs(item.items.map((x) => (x.type == 'desktopgroup' ? React.createRef<HTMLButtonElement>() : React.createRef<HTMLAnchorElement>())));
    setAllSubMenuRefs(item.items.map((x) => (x.type == 'desktopgroup' ? React.createRef<HTMLUListElement>() : undefined)));
  }, [item]);

  const existingRef = dropdownMenusOpenedArray.current.find((x) => x == item.id);
  const onMouseLeave = (event: any, id: string) => {
    dropdownMenusOpenedArray.current = [...dropdownMenusOpenedArray.current.filter((x) => x != id)];
    //just trigger rerendering
    onSubMenuHidden && onSubMenuHidden(id);
    forceUpdate({});
  };
  let containActiveItem = false;
  if (item.type == 'desktopgroup' && activeItemId) containActiveItem = checkIfContainsItem(item, activeItemId);

  const onMouseEnter = (event: any, id: string) => {
    const existingRefForId = dropdownMenusOpenedArray.current.find((x) => x == id);
    if (existingRefForId == null) {
      dropdownMenusOpenedArray.current = [...dropdownMenusOpenedArray.current, id];
      //just trigger rerendering
      onSubMenuOpened && onSubMenuOpened(id);
      forceUpdate({});
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    
    if (e.key === 'ArrowUp' || e.key === 'Up') {
      e.stopPropagation();
      e.preventDefault();
      if (focusedRef.current.index > 0) {
        setNewFocusedElement(focusedRef.current.index - 1);
      }
    } else if (e.key === 'ArrowDown' || e.key === 'Down') {
      e.stopPropagation();
      e.preventDefault();
      if(focusedRef.current.index < allRefs.length - 1)
      setNewFocusedElement(focusedRef.current.index + 1);
    } 
     else
    if (e.key == 'Enter' || e.code == 'Space') {
      e.stopPropagation();
      if(focusedRef.current.index < 0)
        return;
      
      const focusedItem = item.items[focusedRef.current.index];
      if(focusedItem.type == 'desktopgroup')
      {
        dropdownMenusOpenedArray.current = [...dropdownMenusOpenedArray.current, focusedItem.id];
        forceUpdate({});
      }
      else
      {
        allRefs[focusedRef.current.index].current?.click();
        forceUpdate({});
      }

      //just trigger rerendering
      
    }
  };

  const setNewFocusedElement = (index: number) => {
    const newFocusedElement = index >= 0 ? allRefs[index] : null;
    const oldFocusedElement = focusedRef.current.index >= 0 ? allRefs[focusedRef.current.index] : null;
    focusedRef.current.index = index;

    if(oldFocusedElement)
      oldFocusedElement?.current?.classList.remove('dropdown-hover');

    if (newFocusedElement && newFocusedElement !== oldFocusedElement) {
      newFocusedElement.current?.classList.add('dropdown-hover');
      onItemFocused && onItemFocused(item.items[index].id ?? '');
    }
    else if(onItemFocused)
      onItemFocused('');
  };

  const onFocus = (event: React.FocusEvent<HTMLUListElement>) => {
    event.stopPropagation();
    dropdownMenusOpenedArray.current = [...(nestPath ?? [])];
    setNewFocusedElement(0);
    forceUpdate({});
    
  };

  const onBlur = (event: React.FocusEvent<HTMLUListElement>) => {
    event.stopPropagation();
    setNewFocusedElement(-1);
  };

  return (
    <SubMenuWrapper id={item.id}  className={existingRef ? 'open' : '' } $topLevel={topLevel} $visible={Boolean(existingRef)}>
      <Menu role="group" ref={ref} aria-labelledby={ariaLabelledBy} onBlur={onBlur} onFocus={onFocus} onKeyDown={(event) => onKeyDown(event)} tabIndex={0}>
        <MenuSection>
          <MenuSectionList>
            <Tabs size={Size.Medium}>
              {item.items.map((entry, index) => {
                const isGroup = entry.type == 'desktopgroup';
                const key = isGroup ? entry.id : entry.type == 'item' ? entry.to : '';
                const SubItemOpened = Boolean(dropdownMenusOpenedArray.current.find((x) => x == entry.id));
                if (isGroup) {
                  return (
                    <div style={{ position: 'relative' }} onMouseEnter={(event) => onMouseEnter(event, entry.id)} onMouseLeave={(event) => onMouseLeave(event, entry.id)}>
                      <MenuItem
                        id={entry.id + '_parentOption'}
                        aria-expanded={Boolean(SubItemOpened)}
                        aria-activedescendant={entry.items.find(x => x.id == focusedItemId)?.id ?? ''}
                        tabIndex={-1}
                        ref={allRefs[index] as React.Ref<HTMLButtonElement>}
                        
                        aria-controls={entry.id}
                        role="group"
                        //id={entry.id} removing this can break something
                        item={{
                          value: entry.label ?? '',
                        }}
                        iconRight={<SystemIcons.ArrowDropRight size="24px" />}
                        active={SubItemOpened || (Boolean(activeItemId) && activeItemId != '' && checkIfContainsItem(entry, activeItemId!))}
                        onClickHandler={() => {}}
                      />
                      <SubMenu
                        ariaLabelledBy={entry.id + '_parentOption'}
                        onItemFocused={onItemFocused}
                        focusedItemId={focusedItemId}
                        ref={subMenuRefs[index]}
                        nestPath={[...(nestPath ?? []), entry.id]}
                        onItemStateChanged={onItemStateChanged}
                        activeItemId={activeItemId}
                        dropdownMenusOpenedArray={dropdownMenusOpenedArray}
                        item={entry as MenuNavigationItemTypeDesktopGroup}
                        index={index + 1}
                        topLevel={false}
                      />
                    </div>
                  );
                }
                return (
                  <>
                    <TabLink
                      id={entry.id}
                      tabIndex={-1}
                      key={key}
                      href={isGroup ? '' : key}
                      ref={allRefs[index] as React.Ref<HTMLAnchorElement>}
                      onActiveStateChanged={(state) => onItemStateChanged && entry.type == 'item' && onItemStateChanged(entry.to, state)}
                      forceDeactivate={isGroup}
                      requiredLine={entry.label ?? ''}
                      optionalLine={''}
                      endLineIcon={isGroup ? <SystemIcons.ArrowDropRight size="24px" /> : null}
                      OptionalLineIcon={''}
                      disabled={entry.disabled}
                      showNotificationDot={false}
                      size={Size.Medium}
                      variant="positive"
                    />
                  </>
                );
              })}
            </Tabs>
          </MenuSectionList>
        </MenuSection>
      </Menu>
    </SubMenuWrapper>
  );
});
